import { z } from 'zod';
import { CountryCodeSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Notifications (W18): Capsico's own engine.                          */
/*                                                                     */
/* Every message is an event in an outbox (notify_events). The         */
/* dispatcher renders the template for the person's language, asks the */
/* gatekeeper (consent, weekly cap per device, quiet hours, dedupe,     */
/* suppression, kill switch, holdout) and sends on the allowed          */
/* channels: FCM push, SMS (provider templates), WhatsApp (seam) and    */
/* the in-app inbox, which keeps every message (system of record).     */
/* ------------------------------------------------------------------ */

const Id = z.uuid();

/** Ops screens poll at this pace (sends log, outbox). */
export const NOTIFY_POLL_SECONDS = 30;
/** Inbox page size for the apps. */
export const INBOX_PAGE_SIZE = 30;
/** Longest title and body a template may render to. */
export const NOTIFY_TITLE_MAX = 80;
export const NOTIFY_BODY_MAX = 400;
/** SMS bodies are cut to one long message. */
export const NOTIFY_SMS_MAX = 300;

/* ---------------- vocabulary ---------------- */

export const NOTIFY_CHANNELS = ['push', 'sms', 'whatsapp', 'inbox'] as const;
export const NotifyChannelSchema = z.enum(NOTIFY_CHANNELS);
export type NotifyChannel = z.infer<typeof NotifyChannelSchema>;
export const NOTIFY_CHANNEL_LABELS: Record<NotifyChannel, string> = {
  push: 'Push',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  inbox: 'In-app inbox',
};

/**
 * orders / support / account are transactional (always sent, never capped, never quiet-deferred).
 * offers / reminders are marketing: consent, the weekly cap and quiet hours apply.
 */
export const NOTIFY_CATEGORIES = ['orders', 'support', 'account', 'offers', 'reminders'] as const;
export const NotifyCategorySchema = z.enum(NOTIFY_CATEGORIES);
export type NotifyCategory = z.infer<typeof NotifyCategorySchema>;
export const NOTIFY_CATEGORY_LABELS: Record<NotifyCategory, string> = {
  orders: 'Orders',
  support: 'Help & support',
  account: 'Account & money',
  offers: 'Offers & deals',
  reminders: 'Reminders',
};
export const MARKETING_CATEGORIES: readonly NotifyCategory[] = ['offers', 'reminders'];
export const isMarketingCategory = (category: NotifyCategory): boolean =>
  MARKETING_CATEGORIES.includes(category);

export const NOTIFY_AUDIENCES = ['customer', 'partner', 'rider'] as const;
export const NotifyAudienceSchema = z.enum(NOTIFY_AUDIENCES);
export type NotifyAudience = z.infer<typeof NotifyAudienceSchema>;
export const NOTIFY_AUDIENCE_LABELS: Record<NotifyAudience, string> = {
  customer: 'Customers',
  partner: 'Restaurant partners',
  rider: 'Riders',
};

export const NOTIFY_LOCALES = ['en', 'hi', 'ne'] as const;
export const NotifyLocaleSchema = z.enum(NOTIFY_LOCALES);
export type NotifyLocale = z.infer<typeof NotifyLocaleSchema>;
export const NOTIFY_LOCALE_LABELS: Record<NotifyLocale, string> = {
  en: 'English',
  hi: 'हिन्दी',
  ne: 'नेपाली',
};

/** Every message Capsico can send. Each has templates per channel + language. */
export const NOTIFY_TEMPLATE_KEYS = [
  // customer — transactional
  'order_accepted',
  'rider_assigned',
  'rider_nearby',
  'order_delivered',
  'order_cancelled',
  'late_apology',
  'refund_issued',
  'refund_choice_needed',
  'delivery_code',
  'support_reply',
  'referral_reward',
  'cashback_credited',
  'campus_pass_approved',
  'campus_pass_rejected',
  // customer — marketing journeys
  'cart_abandoned',
  'first_order_nudge',
  'win_back',
  'offer_launch',
  'rate_order',
  // partner
  'payout_ready',
  'plan_bill',
  // rider
  'rider_payout_ready',
  'shift_reminder',
] as const;
export const NotifyTemplateKeySchema = z.enum(NOTIFY_TEMPLATE_KEYS);
export type NotifyTemplateKey = z.infer<typeof NotifyTemplateKeySchema>;

export interface NotifyKeyDef {
  audience: NotifyAudience;
  category: NotifyCategory;
  label: string;
  /** Variables the template may use, e.g. {{orderNo}}. */
  variables: readonly string[];
}

/** The fixed catalogue: who gets each message and in which category. */
export const NOTIFY_KEY_DEFS: Record<NotifyTemplateKey, NotifyKeyDef> = {
  order_accepted: {
    audience: 'customer',
    category: 'orders',
    label: 'Order accepted',
    variables: ['name', 'orderNo', 'restaurant', 'eta'],
  },
  rider_assigned: {
    audience: 'customer',
    category: 'orders',
    label: 'Rider assigned',
    variables: ['name', 'orderNo', 'rider'],
  },
  rider_nearby: {
    audience: 'customer',
    category: 'orders',
    label: 'Rider is nearby',
    variables: ['name', 'orderNo'],
  },
  order_delivered: {
    audience: 'customer',
    category: 'orders',
    label: 'Order delivered',
    variables: ['name', 'orderNo', 'restaurant'],
  },
  order_cancelled: {
    audience: 'customer',
    category: 'orders',
    label: 'Order cancelled',
    variables: ['name', 'orderNo', 'reason'],
  },
  late_apology: {
    audience: 'customer',
    category: 'orders',
    label: 'Late delivery apology',
    variables: ['name', 'orderNo', 'amount'],
  },
  refund_issued: {
    audience: 'customer',
    category: 'account',
    label: 'Refund issued',
    variables: ['name', 'orderNo', 'amount'],
  },
  refund_choice_needed: {
    audience: 'customer',
    category: 'account',
    label: 'Refund: choose how',
    variables: ['name', 'orderNo', 'amount'],
  },
  delivery_code: {
    audience: 'customer',
    category: 'orders',
    label: 'Delivery code',
    variables: ['name', 'orderNo', 'code'],
  },
  support_reply: {
    audience: 'customer',
    category: 'support',
    label: 'Support replied',
    variables: ['name', 'ticketNo'],
  },
  referral_reward: {
    audience: 'customer',
    category: 'account',
    label: 'Referral reward',
    variables: ['name', 'amount', 'friend'],
  },
  cashback_credited: {
    audience: 'customer',
    category: 'account',
    label: 'Cashback credited',
    variables: ['name', 'amount', 'orderNo'],
  },
  campus_pass_approved: {
    audience: 'customer',
    category: 'account',
    label: 'Campus Pass approved',
    variables: ['name', 'campus', 'validUntil'],
  },
  campus_pass_rejected: {
    audience: 'customer',
    category: 'account',
    label: 'Campus Pass not approved',
    variables: ['name', 'campus', 'reason'],
  },
  cart_abandoned: {
    audience: 'customer',
    category: 'reminders',
    label: 'Cart left behind',
    variables: ['name', 'restaurant'],
  },
  first_order_nudge: {
    audience: 'customer',
    category: 'offers',
    label: 'First-order nudge',
    variables: ['name', 'offerCode'],
  },
  win_back: {
    audience: 'customer',
    category: 'offers',
    label: 'Win-back',
    variables: ['name', 'amount', 'offerCode'],
  },
  offer_launch: {
    audience: 'customer',
    category: 'offers',
    label: 'Offer launch',
    variables: ['name', 'offerCode', 'offerTitle'],
  },
  rate_order: {
    audience: 'customer',
    category: 'reminders',
    label: 'Rate your order',
    variables: ['name', 'orderNo', 'restaurant'],
  },
  payout_ready: {
    audience: 'partner',
    category: 'account',
    label: 'Payout ready',
    variables: ['name', 'amount', 'period'],
  },
  plan_bill: {
    audience: 'partner',
    category: 'account',
    label: 'Plan bill',
    variables: ['name', 'amount', 'dueDate'],
  },
  rider_payout_ready: {
    audience: 'rider',
    category: 'account',
    label: 'Rider payout ready',
    variables: ['name', 'amount', 'period'],
  },
  shift_reminder: {
    audience: 'rider',
    category: 'account',
    label: 'Shift reminder',
    variables: ['name', 'shiftStart', 'zone'],
  },
};

export const NOTIFY_TEMPLATE_STATUSES = ['draft', 'live', 'retired'] as const;
export const NotifyTemplateStatusSchema = z.enum(NOTIFY_TEMPLATE_STATUSES);
export type NotifyTemplateStatus = z.infer<typeof NotifyTemplateStatusSchema>;
export const NOTIFY_TEMPLATE_STATUS_LABELS: Record<NotifyTemplateStatus, string> = {
  draft: 'Draft',
  live: 'Live',
  retired: 'Retired',
};

/**
 * What happened to one send. sent = handed to the provider (or stored in the inbox). The rest are
 * gatekeeper outcomes (nothing left Capsico) or provider failures.
 */
export const NOTIFY_SEND_STATUSES = [
  'sent',
  'failed',
  'no_device',
  'opted_out',
  'capped',
  'quiet_deferred',
  'deduped',
  'suppressed',
  'killed',
  'holdout',
] as const;
export const NotifySendStatusSchema = z.enum(NOTIFY_SEND_STATUSES);
export type NotifySendStatus = z.infer<typeof NotifySendStatusSchema>;
export const NOTIFY_SEND_STATUS_LABELS: Record<NotifySendStatus, string> = {
  sent: 'Sent',
  failed: 'Failed',
  no_device: 'No device',
  opted_out: 'Opted out',
  capped: 'Weekly cap reached',
  quiet_deferred: 'Held for quiet hours',
  deduped: 'Duplicate',
  suppressed: 'Suppressed',
  killed: 'Template switched off',
  holdout: 'Holdout group',
};

export const NOTIFY_EVENT_STATUSES = ['pending', 'processing', 'done', 'failed'] as const;
export type NotifyEventStatus = (typeof NOTIFY_EVENT_STATUSES)[number];

/* ---------------- deep links ---------------- */

/** App links: capsico://<path>. The web fallback uses the same path on the customer web app. */
export const DEEP_LINK_SCHEME = 'capsico://';
export const deepLinks = {
  order: (orderId: string) => `${DEEP_LINK_SCHEME}order/${orderId}`,
  orderRate: (orderId: string) => `${DEEP_LINK_SCHEME}order/${orderId}/rate`,
  restaurant: (outletId: string, offerCode?: string | null) =>
    `${DEEP_LINK_SCHEME}restaurant/${outletId}${offerCode ? `?offer=${encodeURIComponent(offerCode)}` : ''}`,
  wallet: () => `${DEEP_LINK_SCHEME}wallet`,
  offers: (code?: string | null) =>
    `${DEEP_LINK_SCHEME}offers${code ? `?code=${encodeURIComponent(code)}` : ''}`,
  ticket: (ticketId: string) => `${DEEP_LINK_SCHEME}help/${ticketId}`,
  inbox: () => `${DEEP_LINK_SCHEME}inbox`,
  referral: () => `${DEEP_LINK_SCHEME}refer`,
  campusPass: () => `${DEEP_LINK_SCHEME}campus-pass`,
  cart: () => `${DEEP_LINK_SCHEME}cart`,
  home: () => `${DEEP_LINK_SCHEME}home`,
};
/** capsico://order/abc → /order/abc (web path for the customer web app). */
export function deepLinkWebPath(link: string): string {
  return link.startsWith(DEEP_LINK_SCHEME) ? `/${link.slice(DEEP_LINK_SCHEME.length)}` : link;
}

/* ---------------- templates (Capsico OS) ---------------- */

export const NotifyTemplateSchema = z.object({
  id: Id,
  key: NotifyTemplateKeySchema,
  channel: NotifyChannelSchema,
  locale: NotifyLocaleSchema,
  version: z.number().int(),
  status: NotifyTemplateStatusSchema,
  title: z.string(),
  body: z.string(),
  /** capsico:// link with {{variables}}; null = no tap action. */
  deepLink: z.string().nullable(),
  /** Provider template id (MSG91 flow / DLT, WhatsApp template name). */
  providerTemplateId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  publishedAt: z.string().nullable(),
});
export type NotifyTemplate = z.infer<typeof NotifyTemplateSchema>;

export const NotifyTemplateKeyRowSchema = z.object({
  key: NotifyTemplateKeySchema,
  label: z.string(),
  audience: NotifyAudienceSchema,
  category: NotifyCategorySchema,
  transactional: z.boolean(),
  variables: z.array(z.string()),
  /** Kill switch: false stops every send of this message at once. */
  enabled: z.boolean(),
  killReason: z.string().nullable(),
  /** Live template per channel + language ("push:en"). */
  live: z.array(z.string()),
  sent7d: z.number().int(),
  version: z.number().int(),
});
export type NotifyTemplateKeyRow = z.infer<typeof NotifyTemplateKeyRowSchema>;

export const NotifyTemplateKeyListSchema = z.object({ rows: z.array(NotifyTemplateKeyRowSchema) });
export type NotifyTemplateKeyList = z.infer<typeof NotifyTemplateKeyListSchema>;

export const NotifyTemplateDetailSchema = z.object({
  key: NotifyTemplateKeyRowSchema,
  templates: z.array(NotifyTemplateSchema),
});
export type NotifyTemplateDetail = z.infer<typeof NotifyTemplateDetailSchema>;

export const NotifyTemplateInputSchema = z.object({
  channel: NotifyChannelSchema,
  locale: NotifyLocaleSchema,
  title: z.string().trim().max(NOTIFY_TITLE_MAX).default(''),
  body: z.string().trim().min(2, 'Write the message').max(NOTIFY_BODY_MAX),
  deepLink: z
    .string()
    .trim()
    .max(300)
    .nullable()
    .default(null)
    .transform((value) => (value === '' ? null : value))
    .refine((value) => value === null || value.startsWith(DEEP_LINK_SCHEME), {
      message: `Links start with ${DEEP_LINK_SCHEME}`,
    }),
  providerTemplateId: z
    .string()
    .trim()
    .max(80)
    .nullable()
    .default(null)
    .transform((value) => (value === '' ? null : value)),
});
export type NotifyTemplateInput = z.infer<typeof NotifyTemplateInputSchema>;

export const NotifyTemplatePublishInputSchema = z.object({
  /** Publishing retires the live version of the same channel + language. */
  note: z.string().trim().max(200).default(''),
});
export type NotifyTemplatePublishInput = z.infer<typeof NotifyTemplatePublishInputSchema>;

export const NotifyKillInputSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().trim().min(3, 'Say why (at least 3 characters)').max(200),
  expectedVersion: z.number().int().min(1),
});
export type NotifyKillInput = z.infer<typeof NotifyKillInputSchema>;

const VarsSchema = z.record(z.string().max(40), z.string().max(200));

export const NotifyPreviewInputSchema = z.object({
  title: z.string().max(NOTIFY_TITLE_MAX).default(''),
  body: z.string().max(NOTIFY_BODY_MAX),
  deepLink: z.string().max(300).nullable().default(null),
  vars: VarsSchema.default({}),
});
export type NotifyPreviewInput = z.infer<typeof NotifyPreviewInputSchema>;

export const NotifyPreviewSchema = z.object({
  title: z.string(),
  body: z.string(),
  deepLink: z.string().nullable(),
  webPath: z.string().nullable(),
  /** Variables the text used but the sample did not give (rendered with the fallback). */
  missing: z.array(z.string()),
  /** Unknown {{tags}} the template uses (not in the key's variable list). */
  unknown: z.array(z.string()),
});
export type NotifyPreview = z.infer<typeof NotifyPreviewSchema>;

export const NotifyTestSendInputSchema = z.object({
  templateId: Id,
  /** A staff member's own phone (customer identity with that phone) — the send skips the gatekeeper. */
  phone: z
    .string()
    .trim()
    .regex(/^\+(91[6-9]\d{9}|9779[678]\d{8})$/, 'Enter a valid Indian or Nepali mobile number'),
  vars: VarsSchema.default({}),
});
export type NotifyTestSendInput = z.infer<typeof NotifyTestSendInputSchema>;

export const NotifyTestSendResultSchema = z.object({
  status: NotifySendStatusSchema,
  channel: NotifyChannelSchema,
  provider: z.string(),
  detail: z.string().nullable(),
  rendered: NotifyPreviewSchema,
});
export type NotifyTestSendResult = z.infer<typeof NotifyTestSendResultSchema>;

/* ---------------- journeys ---------------- */

export const NOTIFY_JOURNEY_KEYS = [
  'cart_abandoned',
  'first_order_nudge',
  'win_back',
  'offer_launch',
  'rate_order',
] as const;
export const NotifyJourneyKeySchema = z.enum(NOTIFY_JOURNEY_KEYS);
export type NotifyJourneyKey = z.infer<typeof NotifyJourneyKeySchema>;
export const NOTIFY_JOURNEY_LABELS: Record<NotifyJourneyKey, string> = {
  cart_abandoned: 'Cart left behind (30 min, once)',
  first_order_nudge: 'First-order nudge (24 h after sign-up)',
  win_back: 'Win-back (30 days since the last order)',
  offer_launch: 'Offer launch (segment)',
  rate_order: 'Rate your order (2 h after delivery)',
};

export const NotifyJourneySegmentSchema = z.object({
  country: CountryCodeSchema.nullable().default(null),
  townIds: z.array(Id).max(50).default([]),
});
export type NotifyJourneySegment = z.infer<typeof NotifyJourneySegmentSchema>;

export const NotifyJourneySchema = z.object({
  key: NotifyJourneyKeySchema,
  label: z.string(),
  templateKey: NotifyTemplateKeySchema,
  enabled: z.boolean(),
  /**
   * Wait after the trigger (cart idle, sign-up, delivery). Win-back ignores it: its timing is the
   * growth.winback_after_days setting.
   */
  delayMinutes: z.number().int(),
  /** Share of people kept out (measured against) — by device hash. */
  holdoutPercent: z.number().int(),
  /** Do not send again to the same person within this many days (0 = once per trigger). */
  oncePerDays: z.number().int(),
  segment: NotifyJourneySegmentSchema,
  stats: z.object({
    sent7d: z.number().int(),
    holdout7d: z.number().int(),
    converted7d: z.number().int(),
    blocked7d: z.number().int(),
  }),
  lastRunAt: z.string().nullable(),
  version: z.number().int(),
});
export type NotifyJourney = z.infer<typeof NotifyJourneySchema>;

export const NotifyJourneyListSchema = z.object({ rows: z.array(NotifyJourneySchema) });
export type NotifyJourneyList = z.infer<typeof NotifyJourneyListSchema>;

export const NotifyJourneyUpdateInputSchema = z.object({
  enabled: z.boolean(),
  delayMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 120),
  holdoutPercent: z.number().int().min(0).max(50),
  oncePerDays: z.number().int().min(0).max(365),
  segment: NotifyJourneySegmentSchema.default({ country: null, townIds: [] }),
  expectedVersion: z.number().int().min(1),
});
export type NotifyJourneyUpdateInput = z.infer<typeof NotifyJourneyUpdateInputSchema>;

/** Sends the offer-launch journey now to its segment (one run per person per offer). */
export const OfferLaunchInputSchema = z.object({
  offerId: Id,
  country: CountryCodeSchema,
  townIds: z.array(Id).max(50).default([]),
});
export type OfferLaunchInput = z.infer<typeof OfferLaunchInputSchema>;

export const OfferLaunchResultSchema = z.object({
  queued: z.number().int(),
  holdout: z.number().int(),
  skipped: z.number().int(),
});
export type OfferLaunchResult = z.infer<typeof OfferLaunchResultSchema>;

/* ---------------- sends log ---------------- */

export const NotifySendRowSchema = z.object({
  id: Id,
  eventId: z.string(),
  key: NotifyTemplateKeySchema,
  category: NotifyCategorySchema,
  channel: NotifyChannelSchema,
  status: NotifySendStatusSchema,
  identityId: Id,
  recipient: z.string(),
  audience: NotifyAudienceSchema,
  locale: NotifyLocaleSchema,
  title: z.string(),
  body: z.string(),
  deepLink: z.string().nullable(),
  provider: z.string(),
  detail: z.string().nullable(),
  templateVersion: z.number().int().nullable(),
  journey: NotifyJourneyKeySchema.nullable(),
  at: z.string(),
});
export type NotifySendRow = z.infer<typeof NotifySendRowSchema>;

export const NotifySendQuerySchema = z.object({
  status: NotifySendStatusSchema.optional(),
  channel: NotifyChannelSchema.optional(),
  key: NotifyTemplateKeySchema.optional(),
  identityId: Id.optional(),
  search: z.string().trim().max(40).optional(),
  page: z.coerce.number().int().min(1).max(1000).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type NotifySendQuery = z.infer<typeof NotifySendQuerySchema>;

export const NotifySendListSchema = z.object({
  rows: z.array(NotifySendRowSchema),
  total: z.number().int(),
  counts: z.record(z.string(), z.number().int()),
});
export type NotifySendList = z.infer<typeof NotifySendListSchema>;

/* ---------------- settings + summary ---------------- */

export const NotifySettingsSchema = z.object({
  capPerWeek: z.number().int(),
  quietFrom: z.number().int(),
  quietTo: z.number().int(),
  providers: z.object({
    push: z.string(),
    smsIN: z.string(),
    smsNP: z.string(),
    whatsapp: z.string(),
  }),
  /** Config keys behind the numbers (edited in Settings, audited there). */
  keys: z.object({
    capPerWeek: z.string(),
    quietFrom: z.string(),
    quietTo: z.string(),
  }),
});
export type NotifySettings = z.infer<typeof NotifySettingsSchema>;

export const NotifySummarySchema = z.object({
  sent24h: z.number().int(),
  failed24h: z.number().int(),
  capped24h: z.number().int(),
  deferred: z.number().int(),
  outboxPending: z.number().int(),
  pushDevices: z.number().int(),
  killedKeys: z.number().int(),
  lastDispatchAt: z.string().nullable(),
  lastJourneysAt: z.string().nullable(),
});
export type NotifySummary = z.infer<typeof NotifySummarySchema>;

export const SuppressionInputSchema = z.object({
  phone: z
    .string()
    .trim()
    .regex(/^\+(91[6-9]\d{9}|9779[678]\d{8})$/, 'Enter a valid Indian or Nepali mobile number'),
  reason: z.string().trim().min(3).max(200),
});
export type SuppressionInput = z.infer<typeof SuppressionInputSchema>;

/* ---------------- apps: inbox, prefs, push devices ---------------- */

export const InboxMessageSchema = z.object({
  id: Id,
  key: NotifyTemplateKeySchema,
  category: NotifyCategorySchema,
  title: z.string(),
  body: z.string(),
  deepLink: z.string().nullable(),
  /** Same path for the web app (capsico://order/x → /order/x). */
  webPath: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type InboxMessage = z.infer<typeof InboxMessageSchema>;

export const NotifyInboxQuerySchema = z.object({
  /** createdAt|id of the last row seen (keyset paging). */
  cursor: z.string().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(INBOX_PAGE_SIZE),
});
export type NotifyInboxQuery = z.infer<typeof NotifyInboxQuerySchema>;

export const InboxPageSchema = z.object({
  rows: z.array(InboxMessageSchema),
  unread: z.number().int(),
  nextCursor: z.string().nullable(),
});
export type InboxPage = z.infer<typeof InboxPageSchema>;

export const InboxUnreadSchema = z.object({ unread: z.number().int() });
export type InboxUnread = z.infer<typeof InboxUnreadSchema>;

export const InboxReadInputSchema = z.object({
  /** Empty = mark everything read. */
  ids: z.array(Id).max(200).default([]),
});
export type InboxReadInput = z.infer<typeof InboxReadInputSchema>;

/** Live socket payload on identity:<id> when a message lands (W14 hub). */
export const InboxLiveEventSchema = z.object({
  message: InboxMessageSchema,
  unread: z.number().int(),
});
export type InboxLiveEvent = z.infer<typeof InboxLiveEventSchema>;

/**
 * Orders, support and account messages are always on. Offers + reminders are opt-in by default
 * (both countries' rules allow it) and the person can switch them off; per-channel switches cut
 * marketing on that channel only.
 */
export const NotifyPrefsSchema = z.object({
  orders: z.literal(true),
  offers: z.boolean(),
  reminders: z.boolean(),
  push: z.boolean(),
  sms: z.boolean(),
  whatsapp: z.boolean(),
  version: z.number().int(),
});
export type NotifyPrefs = z.infer<typeof NotifyPrefsSchema>;

export const NotifyPrefsInputSchema = z.object({
  offers: z.boolean(),
  reminders: z.boolean(),
  push: z.boolean(),
  sms: z.boolean(),
  whatsapp: z.boolean(),
  /** null on the first save. */
  expectedVersion: z.number().int().min(1).nullable(),
});
export type NotifyPrefsInput = z.infer<typeof NotifyPrefsInputSchema>;

export const NOTIFY_PUSH_PLATFORMS = ['android', 'ios', 'web'] as const;
export const PushPlatformSchema = z.enum(NOTIFY_PUSH_PLATFORMS);
export type PushPlatform = z.infer<typeof PushPlatformSchema>;

export const PushRegisterInputSchema = z.object({
  token: z.string().trim().min(20).max(4096),
  platform: PushPlatformSchema,
  /** The app's stable install id; stored only as a salted hash (weekly cap per device). */
  deviceId: z.string().trim().min(8).max(200).optional(),
  locale: NotifyLocaleSchema.optional(),
});
export type PushRegisterInput = z.infer<typeof PushRegisterInputSchema>;

export const PushUnregisterInputSchema = z.object({
  token: z.string().trim().min(20).max(4096),
});
export type PushUnregisterInput = z.infer<typeof PushUnregisterInputSchema>;

export const PushRegisterResultSchema = z.object({
  registered: z.boolean(),
  devices: z.number().int(),
});
export type PushRegisterResult = z.infer<typeof PushRegisterResultSchema>;

/** Customer app: the cart changed (powers "cart left behind"). Clear with itemCount 0. */
export const CartSignalInputSchema = z.object({
  outletId: Id,
  itemCount: z.number().int().min(0).max(200),
  totalMinor: z.number().int().min(0).max(100_000_000),
});
export type CartSignalInput = z.infer<typeof CartSignalInputSchema>;

/** Data-only FCM payload keys the apps read (strings only). */
export const PUSH_DATA_KEYS = {
  type: 'type',
  messageId: 'messageId',
  key: 'key',
  title: 'title',
  body: 'body',
  link: 'link',
} as const;
/** data.type of a notification push (the order ring keeps its own types). */
export const PUSH_TYPE_NOTIFICATION = 'notify';
